import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon, CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { ReviewCard } from '../components/ReviewCard';
import { Button } from '../components/Button';
import { exchanges } from '../data/mockData';
export function ExchangeDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const exchange = exchanges.find(e => e.id === id);
  if (!exchange) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-211">
        <div className="text-center" data-id="element-212">
          <h1 className="text-2xl font-bold mb-4" data-id="element-213">Exchange not found</h1>
          <Link to="/exchanges" data-id="element-214">
            <Button data-id="element-215">Back to Exchanges</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-216">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-217">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
          <Link to="/exchanges" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors" data-id="element-219">
            <ArrowLeftIcon size={20} data-id="element-220" />
            <span data-id="element-221">Back to Exchanges</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-222">
            <div className="flex items-center gap-4" data-id="element-223">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold text-2xl" data-id="element-224">
                {exchange.rank}
              </div>
              <div data-id="element-225">
                <h1 className="text-4xl font-bold mb-2" data-id="element-226">{exchange.name}</h1>
                <TrustScore score={exchange.trustScore} size="md" data-id="element-227" />
              </div>
            </div>

            <a href={exchange.website} target="_blank" rel="noopener noreferrer" data-id="element-228">
              <Button size="lg" className="flex items-center gap-2" data-id="element-229">
                <span data-id="element-230">Visit Exchange</span>
                <ExternalLinkIcon size={18} data-id="element-231" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-232">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-233">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-234">
            {/* Key Metrics */}
            <Card className="p-6" data-id="element-235">
              <h2 className="text-2xl font-bold mb-6" data-id="element-236">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-237">
                <div data-id="element-238">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-239">
                    24h Trading Volume
                  </div>
                  <div className="text-xl font-semibold" data-id="element-240">
                    ${(exchange.tradingVolume / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-241">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-242">
                    Supported Coins
                  </div>
                  <div className="text-xl font-semibold" data-id="element-243">
                    {exchange.supportedCoins}
                  </div>
                </div>
                <div data-id="element-244">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-245">Trading Fee</div>
                  <div className="text-xl font-semibold" data-id="element-246">
                    {exchange.tradingFee}
                  </div>
                </div>
                <div data-id="element-247">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-248">
                    Withdrawal Fee
                  </div>
                  <div className="text-xl font-semibold" data-id="element-249">
                    {exchange.withdrawalFee}
                  </div>
                </div>
              </div>
            </Card>

            {/* About */}
            <Card className="p-6" data-id="element-250">
              <h2 className="text-2xl font-bold mb-4" data-id="element-251">About {exchange.name}</h2>
              <p className="text-slate-700 leading-relaxed mb-6" data-id="element-252">
                {exchange.description}
              </p>
            </Card>

            {/* Features */}
            <Card className="p-6" data-id="element-253">
              <h2 className="text-2xl font-bold mb-4" data-id="element-254">Features</h2>
              <div className="flex flex-wrap gap-2" data-id="element-255">
                {exchange.features.map((feature, index) => <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-256">
                    {feature}
                  </span>)}
              </div>
            </Card>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-257">
              <Card className="p-6" data-id="element-258">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-259">
                  <CheckIcon className="text-green-600" size={24} data-id="element-260" />
                  <span data-id="element-261">Pros</span>
                </h3>
                <ul className="space-y-2" data-id="element-262">
                  {exchange.pros.map((pro, index) => <li key={index} className="flex items-start gap-2" data-id="element-263">
                      <CheckIcon className="text-green-600 flex-shrink-0 mt-0.5" size={16} data-id="element-264" />
                      <span className="text-slate-700" data-id="element-265">{pro}</span>
                    </li>)}
                </ul>
              </Card>

              <Card className="p-6" data-id="element-266">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-267">
                  <XIcon className="text-red-600" size={24} data-id="element-268" />
                  <span data-id="element-269">Cons</span>
                </h3>
                <ul className="space-y-2" data-id="element-270">
                  {exchange.cons.map((con, index) => <li key={index} className="flex items-start gap-2" data-id="element-271">
                      <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={16} data-id="element-272" />
                      <span className="text-slate-700" data-id="element-273">{con}</span>
                    </li>)}
                </ul>
              </Card>
            </div>

            {/* Reviews */}
            <div data-id="element-274">
              <h2 className="text-2xl font-bold mb-6" data-id="element-275">User Reviews</h2>
              <div className="space-y-4" data-id="element-276">
                {exchange.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-277" />)}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-278">
            <Card className="p-6" data-id="element-279">
              <h3 className="font-bold text-lg mb-4" data-id="element-280">Quick Stats</h3>
              <div className="space-y-4" data-id="element-281">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-282">
                  <span className="text-slate-600" data-id="element-283">Rank</span>
                  <span className="font-semibold" data-id="element-284">#{exchange.rank}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-285">
                  <span className="text-slate-600" data-id="element-286">Trading Fee</span>
                  <span className="font-semibold" data-id="element-287">{exchange.tradingFee}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-288">
                  <span className="text-slate-600" data-id="element-289">Coins Listed</span>
                  <span className="font-semibold" data-id="element-290">
                    {exchange.supportedCoins}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-291">
                  <span className="text-slate-600" data-id="element-292">Total Reviews</span>
                  <span className="font-semibold" data-id="element-293">
                    {exchange.reviews.length}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-294">
              <h3 className="font-bold text-lg mb-2" data-id="element-295">Trust Score Breakdown</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4" data-id="element-296">
                {exchange.trustScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-297">
                Our trust score evaluates security measures, regulatory
                compliance, user experience, and customer support quality.
              </p>
            </Card>

            <Card className="p-6" data-id="element-298">
              <h3 className="font-bold text-lg mb-4" data-id="element-299">Official Links</h3>
              <a href={exchange.website} target="_blank" rel="noopener noreferrer" className="block" data-id="element-300">
                <Button variant="outline" className="w-full flex items-center justify-center gap-2" data-id="element-301">
                  <span data-id="element-302">Visit Website</span>
                  <ExternalLinkIcon size={16} data-id="element-303" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}